import { useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";

import type { Signal } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { toSignalTypeKey } from "app/pages/sales-intelligence/domain/signals/utils/signal-key";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import { clearLastAutoAppliedCreatedSignalAction } from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import { selectLastAutoAppliedCreatedSignal } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

type Options = {
    panelValue: Partial<LeadsSearchAllFiltersTypes>;
    onPanelValueUpdate: (next: Partial<LeadsSearchAllFiltersTypes>) => void;
};

export const useSyncAutoAppliedCreatedSignalToPanelValue = ({ panelValue, onPanelValueUpdate }: Options): void => {
    const dispatch = useDispatch();
    const lastAutoAppliedCreatedSignal = useSelector(selectLastAutoAppliedCreatedSignal);
    const panelValueRef = useRef(panelValue);

    panelValueRef.current = panelValue;

    useEffect(() => {
        if (!lastAutoAppliedCreatedSignal) {
            return;
        }

        const signalTypeKey = toSignalTypeKey(lastAutoAppliedCreatedSignal.signalType);
        const currentSignals = (panelValueRef.current[signalTypeKey] as readonly Signal[] | undefined) ?? [];

        if (currentSignals.some((signal) => signal.id === lastAutoAppliedCreatedSignal.id)) {
            dispatch(clearLastAutoAppliedCreatedSignalAction());
            return;
        }

        const nextPanelValue: Partial<LeadsSearchAllFiltersTypes> = { ...panelValueRef.current };

        (nextPanelValue[signalTypeKey] as Signal[]) = [...currentSignals, lastAutoAppliedCreatedSignal];

        onPanelValueUpdate(nextPanelValue);

        dispatch(clearLastAutoAppliedCreatedSignalAction());
    }, [dispatch, lastAutoAppliedCreatedSignal, onPanelValueUpdate]);
};
